import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  Injectable,
  Injector,
  Renderer2,
  RendererFactory2
} from '@angular/core';
import { ToastComponent } from '@shared/components/toast/toast.component';
import { ToastType } from '@app/core/types/toast-type';
import { TranslateService } from '@ngx-translate/core';

/**
 * Toast Counter
 */
let toast_id: number = 0;

/**
 * Service responsible for handling and managing toasts notifications.
 *
 * Toasts should only be used for positive or neutral messages, where it doesn't hurt that the user overlooks the message.
 *
 * There can be shown a maximum of 5 toasts at a time - and will be first in, first out. The toasts disappear after a fixed amount of time, but can be paused by hovering over them or clicked away manually.
 *
 *  ```typescript
 *  constructor(private readonly toastService: ToastService) {
 *  // Show a successful toast using shorthand
 *  this.toastService.show('Toast successful');
 *
 *  // Show a another type of toast with more details
 *  this.toastService.show('Some error occured. What can we do?!', 'Error Title', 'error');
 *  }
 *  ```
 *
 */
@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private readonly renderer!: Renderer2;

  /** @ignore */
  public toastArray: { id: number, ref: ComponentRef<ToastComponent> }[] = [];

  /** @ignore */
  public limit: number = 5;

  /** @ignore */
  public container?: HTMLElement;

  /** @ignore */
  constructor(
    private readonly injector: Injector,
    private readonly appRef: ApplicationRef,
    private readonly envInjector: EnvironmentInjector,
    private readonly renderFactory: RendererFactory2,
    private readonly translateService: TranslateService,
  ) {
    /** Inject Toast Container **/
    this.renderer = this.renderFactory.createRenderer(null, null);
    this.container = this.renderer.createElement('div');
    this.container?.classList.add('fixed', 'flex', 'flex-col', 'justify-start', 'flex-col-reverse', 'bottom-10', 'right-10', 'top-10', 'w-96', 'z-50', 'pointer-events-none', 'items-end', 'gap-4',
      'transition-all', 'duration-300',);
    if (this.container) {
      document.body.appendChild(this.container);
    }
  }

  /**
   * Shows a toast with a specific message, optional title and optional type.
   * @param message
   * @param title
   * @param type
   */
  public show(message: string, title?: string, type: ToastType = 'success'): void {
    if (this.toastArray.length >= this.limit) {
      const t = this.toastArray[0];
      this.close(t.id);
    }
    this.toastArray.push(
      {
        id: toast_id++,
        ref: this.injectToast(message, title, type),
      }
    );
  }

  /**
   * Allows for closing a toast with a specific numerical id. Is used by the service and the corresponding component itself and will almost never be used by developers.
   * @param id
   */
  public close(id: number): void {
    const obj = this.toastArray.find(item => item.id === id);
    if (obj) {
      obj.ref.destroy();
      this.toastArray.splice(this.toastArray.indexOf(obj), 1);
    }
  }

  /**
   * Closes all open toasts.
   *
   * Will only be used if it's necessary to kill all toasts.
   */
  public closeAll(): void {
    for (const toast of this.toastArray) {
      toast.ref.instance.close();

      this.toastArray.splice(this.toastArray.indexOf(toast), 1);
    }
  }

  private injectToast(message: string, title?: string, type: ToastType = 'success'): ComponentRef<ToastComponent> {
    const componentRef: ComponentRef<ToastComponent> = createComponent(
      ToastComponent,
      {
        environmentInjector: this.envInjector,
        elementInjector: this.injector,
      });

    /** Inject Params **/
    componentRef.instance.type = type;
    componentRef.instance.id = toast_id - 1;

    if (title) {
      componentRef.instance.title = title;
    }

    if (message) {
      componentRef.instance.message = this.translateService.instant(message);
    }

    this.appRef.attachView(componentRef.hostView);

    this.renderer?.appendChild(this.container, componentRef.location.nativeElement);

    componentRef.changeDetectorRef.detectChanges();

    return componentRef;
  }
}
